import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Container,
  Col,
  Row,
  Card,
  CardBody,
  Alert,
} from "reactstrap";
import axios from "axios";
import logoLight from "../../assets/images/logo-light.svg"; // Update with your logo
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth"; // Ensure this component exists in your project

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      token: token || "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      token: Yup.string().required("Token is required"),
      password: Yup.string().min(6).required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values, { setStatus }) => {
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}api/admin/reset-password`, {
          token: values.token,
          password: values.password,
        });

        setStatus({ type: "success", message: res.data?.message || "Password reset successful!" });
        setTimeout(() => navigate("/login"), 2000);
      } catch (error) {
        console.log(error);
        setStatus({ type: "error", message: error?.response?.data?.message || "Something went wrong" });
      }
    },
  });

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <img src={logoLight} alt="Logo" height="20" className="d-inline-block auth-logo" />
                </div>
                <p className="mt-3 fs-15 fw-medium">Reset your password with Velzon</p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">Reset Password</h5>
                    <p className="text-muted">Set a new password to continue</p>

                    <lord-icon
                      src="https://cdn.lordicon.com/rhvddzym.json"
                      trigger="loop"
                      colors="primary:#0ab39c"
                      className="avatar-xl"
                      style={{ width: "120px", height: "120px" }}
                    ></lord-icon>
                  </div>

                  <Alert className="alert-borderless alert-warning text-center mb-2 mx-2" role="alert">
                    Enter your new password below and confirm it.
                  </Alert>

                  <div className="p-2">
                    {formik.status && (
                      <Alert
                        color={formik.status.type === "success" ? "success" : "danger"}
                        className="text-center mb-3"
                      >
                        {formik.status.message}
                      </Alert>
                    )}

                    <Form onSubmit={formik.handleSubmit}>
                      <FormGroup>
                        <Label for="token">Token</Label>
                        <Input
                          id="token"
                          name="token"
                          type="text"
                          value={formik.values.token}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.token && !!formik.errors.token}
                        />
                        <FormFeedback>{formik.errors.token}</FormFeedback>
                      </FormGroup>

                      <FormGroup>
                        <Label for="password">New Password</Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.password && !!formik.errors.password}
                        />
                        <FormFeedback>{formik.errors.password}</FormFeedback>
                      </FormGroup>

                      <FormGroup>
                        <Label for="confirmPassword">Confirm Password</Label>
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          value={formik.values.confirmPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                        />
                        <FormFeedback>{formik.errors.confirmPassword}</FormFeedback>
                      </FormGroup>

                      <div className="text-center mt-4">
                        <Button color="success" block type="submit">
                          Reset Password
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <p className="mb-0">
                  Remembered your password?{" "}
                  <Link to="/login" className="fw-semibold text-primary text-decoration-underline">
                    Click here
                  </Link>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default ResetPassword;
